import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

export default function Address() {
  const location = useLocation();
  const navigate = useNavigate();

  const cartItems = location.state?.cartItems || [];
  const total = location.state?.total || 0;

  const [loadingLocation, setLoadingLocation] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("accessToken");

  // Current form state
  const [address, setAddress] = useState({
    name: "",
    phone: "",
    house: "",
    road: "",
    pincode: "",
    city: "",
    state: "",
    nearby: "",
    instructions: "",
    type: "House",
    weekend_saturday: true,
    weekend_sunday: true,
  });

  // --------------------------------------------------
  // 1. Fetch saved addresses for logged-in user
  // --------------------------------------------------
  useEffect(() => {
    if (!token) {
      alert("Please login to manage addresses.");
      navigate("/login");
      return;
    }

    const fetchAddresses = async () => {
      setLoadingAddresses(true);
      try {
        const res = await fetch("/api/addresses/", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          setSavedAddresses(Array.isArray(data) ? data : []);
        } else {
          const err = await res.json();
          if (res.status === 401) {
            localStorage.removeItem("accessToken");
            navigate("/login");
          } else {
            console.error("Failed to load addresses:", err);
          }
        }
      } catch (err) {
        console.error("Network error:", err);
      } finally {
        setLoadingAddresses(false);
      }
    };

    fetchAddresses();
  }, [token, navigate]);

  // --------------------------------------------------
  // 2. Geolocation
  // --------------------------------------------------
  const handleUseLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`;
          const res = await fetch(url);
          const data = await res.json();
          const a = data.address;

          setAddress((prev) => ({
            ...prev,
            house: a.house_number || prev.house,
            road: a.road || a.neighbourhood || prev.road,
            pincode: a.postcode || prev.pincode,
            city: a.city || a.town || a.village || prev.city,
            state: a.state || prev.state,
            nearby: a.suburb || a.county || prev.nearby,
          }));
        } catch {
          alert("Unable to fetch address.");
        }
        setLoadingLocation(false);
      },
      () => {
        alert("Failed to get location.");
        setLoadingLocation(false);
      }
    );
  };

  // --------------------------------------------------
  // 3. Save current address
  // --------------------------------------------------
  const handleSaveAddress = async () => {
    const required = ["name", "phone", "house", "road", "city", "state", "pincode"];
    const missing = required.filter((f) => !address[f]?.trim());
    if (missing.length > 0) {
      return alert("Please fill all required fields before saving.");
    }

    setSaving(true);
    try {
      const res = await fetch("/api/addresses/", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(address),
      });

      if (res.ok) {
        const newAddr = await res.json();
        setSavedAddresses((prev) => [newAddr, ...prev]);
        alert("Address saved successfully!");
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to save address.");
      }
    } catch {
      alert("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------
  // 4. Load saved address into form
  // --------------------------------------------------
  const loadSavedAddress = (addr) => {
    setAddress(addr);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --------------------------------------------------
  // 5. Delete saved address
  // --------------------------------------------------
  const deleteSavedAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;

    try {
      const res = await fetch(`/api/addresses/${id}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setSavedAddresses((prev) => prev.filter((a) => a.id !== id));
        alert("Address deleted.");
      } else {
        alert("Failed to delete.");
      }
    } catch {
      alert("Network error.");
    }
  };

  // --------------------------------------------------
  // 6. Continue to Summary
  // --------------------------------------------------
  const handleSaveAndContinue = () => {
    const required = ["name", "phone", "house", "road", "city", "state", "pincode"];
    const missing = required.filter((f) => !address[f]?.trim());
    if (missing.length > 0) {
      return alert("Please fill all required fields to continue.");
    }

    navigate("/summary", { state: { address, cartItems, total } });
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 font-['Roboto']">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight">
          Add Delivery Address
        </h1>

        {/* ---------- SAVED ADDRESSES ---------- */}
        {loadingAddresses ? (
          <div className="mb-8 text-center text-gray-600">Loading saved addresses...</div>
        ) : savedAddresses.length > 0 ? (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Your Saved Addresses</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {savedAddresses.map((addr) => (
                <div
                  key={addr.id}
                  className="bg-white p-4 rounded-xl border border-gray-200 flex justify-between items-start hover:shadow-md transition cursor-pointer"
                  onClick={() => loadSavedAddress(addr)}
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{addr.name}</p>
                    <p className="text-sm text-gray-600">
                      {addr.house}, {addr.road}, {addr.city} - {addr.pincode}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{addr.type}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSavedAddress(addr.id);
                    }}
                    className="ml-2 text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* ---------- MAIN FORM ---------- */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-200 space-y-10">
          {/* Use Location */}
          <button
            onClick={handleUseLocation}
            disabled={loadingLocation}
            className="w-full py-3 md:py-4 bg-gray-900 text-white rounded-xl text-sm md:text-base font-semibold hover:bg-black transition disabled:opacity-70"
          >
            {loadingLocation ? "Fetching location…" : "Use My Current Location"}
          </button>

          {/* Name & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-700 font-medium">Full Name *</label>
              <input
                className="w-full mt-2 p-3 rounded-xl bg-gray-100 border border-gray-300 text-sm focus:border-gray-500 outline-none"
                value={address.name}
                onChange={(e) => setAddress({ ...address, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700 font-medium">Mobile Number *</label>
              <input
                className="w-full mt-2 p-3 rounded-xl bg-gray-100 border border-gray-300 text-sm focus:border-gray-500 outline-none"
                value={address.phone}
                onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                placeholder="Enter phone number"
                type="tel"
              />
            </div>
          </div>

          {/* Address Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              ["House No. / Apartment *", "house"],
              ["Street / Area / Colony *", "road"],
              ["Pincode *", "pincode"],
              ["City *", "city"],
              ["State *", "state"],
              ["Nearby Landmark", "nearby"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="text-sm text-gray-700 font-medium">
                  {label}
                </label>
                <input
                  className="w-full mt-2 p-3 rounded-xl bg-gray-100 border border-gray-300 text-sm focus:border-gray-500 outline-none"
                  value={address[key]}
                  onChange={(e) => setAddress({ ...address, [key]: e.target.value })}
                  placeholder={label.replace(" *", "")}
                />
              </div>
            ))}
          </div>

          {/* Address Type */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Address Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {["House", "Apartment", "Business", "Other"].map((type) => (
                <button
                  key={type}
                  onClick={() => setAddress({ ...address, type })}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    address.type === type
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-gray-100 border-gray-300 hover:border-gray-500"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Weekend Delivery */}
          <div className="space-y-4 text-sm">
            <h3 className="text-lg font-semibold text-gray-800">Weekend Delivery</h3>
            {["Saturday", "Sunday"].map((day) => {
              const field = `weekend_${day.toLowerCase()}`;
              return (
                <div key={day} className="flex items-center justify-between">
                  <span className="text-gray-700">Deliver on {day}s?</span>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setAddress({ ...address, [field]: false })}
                      className={`px-4 py-2 rounded-lg border text-xs transition-all ${
                        address[field] === false
                          ? "bg-red-600 text-white border-red-600"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      No
                    </button>
                    <button
                      onClick={() => setAddress({ ...address, [field]: true })}
                      className={`px-4 py-2 rounded-lg border text-xs transition-all ${
                        address[field] === true
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Delivery Instructions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Delivery Instructions (Optional)
            </h3>
            <textarea
              className="w-full p-4 rounded-xl bg-gray-100 border border-gray-300 text-sm min-h-[120px] resize-none focus:border-gray-500 outline-none"
              placeholder="e.g., Leave at reception, call before delivery..."
              value={address.instructions}
              onChange={(e) => setAddress({ ...address, instructions: e.target.value })}
            />
          </div>

          {/* Save Address Button */}
          <button
            onClick={handleSaveAddress}
            disabled={saving}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-70"
          >
            {saving ? "Saving..." : "Save This Address for Future Use"}
          </button>

          {/* Total & Continue */}
          <div className="flex flex-col md:flex-row justify-between items-center bg-gray-100 p-6 rounded-xl border border-gray-300">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <h3 className="text-sm font-medium text-gray-700">Total Amount</h3>
              <p className="text-3xl font-bold text-gray-900">₹{total.toFixed(2)}</p>
            </div>
            <button
              onClick={handleSaveAndContinue}
              className="px-10 py-3 bg-gray-900 text-white rounded-xl text-sm md:text-base font-semibold hover:bg-black transition"
            >
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}